import { Inject, Injectable, InjectionToken } from '@angular/core';
import { HttpRequest, HttpInterceptor, HttpHandler, HttpEvent } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CommonService } from '../services/common/common.service';
import { catchError, map, Observable, Subject, switchMap, throwError, timeout } from 'rxjs';
import { UIErrorMessages, UIMessages } from '../model/uiMessages';
import { AuthService } from '../services/auth/auth.service';
import { ExternalAssistantAuthService } from '../services/ai/external-assistant-auth.service';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { CoreService } from '../services/core/core.service';

export const DEFAULT_TIMEOUT = new InjectionToken<number>('defaultTimeout');
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  refreshTokenInProgress = false;
  uiMessagesHandled: UIMessages = {
    AnnonVehicles: true,
    'generate-otp': true,
    '/users/Mobile': true,
    // Export to DMS errors are shown by the estimate page as "Export to DMS failed: <error>".
    sendestimatetointegration: true
  };
  isUserUnauthorized: boolean = false;
  uiMessageConfig: UIErrorMessages = {
    401: 'Session expired',
    500: 'Sorry something went wrong. Please try again',
    404: 'Sorry something went wrong. Please try again',
    503: 'Sorry something went wrong. Please try again',
    405: 'Sorry something went wrong. Please try again',
    524: 'Sorry something went wrong. Please try again',
    300: 'Sorry something went wrong. Please try again'
  };
  tokenRefreshedSource = new Subject();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  /**
   * constructor
   * @param commonService
   * @param router
   * @param authService
   * @param defaultTimeout
   */
  constructor(
    private commonService: CommonService,
    @Inject(DEFAULT_TIMEOUT) protected defaultTimeout: number,
    private authService: AuthService,
    private readonly externalAssistantAuthService: ExternalAssistantAuthService,
    private msalService: MsalService,
    private router: Router,
    private readonly coreService: CoreService
  ) {}

  /**
   * get error messages
   * @param request
   * @returns
   */
  getErrorMessages(request: any): boolean {
    const keys = Object.keys(this.uiMessagesHandled);
    for (const k in keys) {
      if (request.url.indexOf(keys[k]) !== -1) {
        return true;
      }
    }
    return false;
  }

  /**
   * error message
   * @param request
   * @param error
   * @returns
   */
  errorMessages(request: any, error: any): void {
    if (this.shouldSkipErrorMessage(request, error)) {
      return;
    }

    let message: any = this.extractErrorMessage(error);

    if (error.status === 401) {
      this.handleUnauthorizedError(error);
      return;
    }

    if (error.status === 409 && (request.url.indexOf('costcentres') !== -1 || request.url.indexOf('CostCentres') !== -1)) {
      const duplicateMsg = error.error?.msg ?? error.error?.message ?? error.error?.Message;
      if (duplicateMsg) {
        this.commonService.showToast(error.status, duplicateMsg);
        return;
      }
    }

    if (request.url.indexOf('BidirectionalConversation') !== -1 && request.method === 'POST' && error.status === 500) {
      this.commonService.showToast(error.status, error.error.errorlst[0].error);
      return;
    }

    if (error.status === 403) {
      if (location.href.includes('/file-claim/')) {
        if (error?.error.Message) {
          this.commonService.showToast(error.status, error?.error.Message);
        }
        return;
      } else if (location.href.includes('quote-assessment/submit-missing-info')) {
        this.router.navigateByUrl(`quote-assessment/verify-otp${location.search}`);
      } else {
        message = 'You don\'t have access rights';
        if (request.url.indexOf('quote') !== -1) {
          this.commonService.accessRight.next(false);
        } else if (request.url.indexOf('repair') !== -1) {
          this.commonService.accessRightRepair.next(false);
        }
        this.router.navigate([ 'accessDenied' ]);
      }
    }

    if (error.status === 400 && request.url.includes('intimation-eligibility-check')) {
      return;
    }

    if (error.status === 400 && request.url.indexOf('vehicleDetailsByVIN') != -1) {
      if (
        error.error?.msg === 'VIN decoding limit is exceeded' ||
        error.error?.msg === 'VIN Decoding limit time period is expired' ||
        error.error?.msg === 'VIN Decoding limit time period is not started'
      ) {
        this.commonService.showInfoToast(5000, error.error?.msg);
      } else {
        this.commonService.showInfoToast(5000, 'The vehicle was not found in the database, please add manually');
      }
      return;
    }

    if (!message || (error.status != 400 && error.status != 401 && error.status != 403)) {
      message = 'Sorry something went wrong. Please try again';
    }

    if (message?.toLowerCase().indexOf('vehicle already exists') === -1 && error.status != 0) {
      if (request.url.indexOf('dashboard') !== -1 && window.location.pathname !== '/home') {
        //TO need to show message when dashbaord API is called and route has been changed
      } else {
        this.commonService.showToast(error.status, message);
      }
    }
  }

  /**
   *
   * @param request
   * @param error
   * @returns
   */
  private shouldSkipErrorMessage(request: any, error: any): boolean {
    if (
      request.url.indexOf('PlatformConfiguration') !== -1 ||
      request.url.indexOf('Token/clear') !== -1 ||
      request.url.indexOf('/External/external-token') !== -1
    ) {
      return true;
    }
    // if (error.status === 409) {
    //   return true;
    // }
    if (this.getErrorMessages(request) && error.status != 401 && error.status != 403) {
      return true;
    }
    return this.isUserUnauthorized;
  }

  /**
   *
   * @param error
   * @returns
   */
  private extractErrorMessage(error: any): any {
    let message: any = this.uiMessageConfig[error.status];
    if (!message) {
      message = error.error?.ErrorMessages
        ? error.error
        : error.error?.errorMessages
        ? error.error
        : error.error?.errorlst
        ? error.error
        : error.error?.message
        ? error.error?.message
        : error.error?.Message
        ? error.error?.Message
        : error.error;
    }

    if (typeof message == 'object' && message) {
      if (message.errorMessages) {
        return message.errorMessages[0].error;
      }
      if (message.errorlst) {
        return message.errorlst[0].Error || message.errorlst[0].error;
      }
      if (message.ErrorMessages) {
        return message.ErrorMessages[0].Error;
      }
      return message.msg;
    }

    return message;
  }

  /**
   *
   * @param error
   */
  private handleUnauthorizedError(error: any): void {
    if (error.error === 'Signed out because your account is signed in from another device') {
      this.openUnauthorizedDialog('Signed out because your account is signed in from another device', () => this.authService.signOut());
      return;
    }

    if (location.href.includes('claim/assignment')) {
      this.openUnauthorizedDialog('Your session has expired.Kindly authenticate again to continue.', () =>
        this.router.navigateByUrl(`claim/verify-otp${location.search}`)
      );
      return;
    }

    if (location.href.includes('quote-assessment/submit-missing-info')) {
      this.openUnauthorizedDialog('Your session has expired.Kindly authenticate again to continue.', () =>
        this.router.navigateByUrl(`quote-assessment/verify-otp${location.search}`)
      );
      return;
    }

    if (location.href.includes('trial-account')) {
      this.authService.signOut();
      this.router.navigateByUrl(`/trial-account`);
      return;
    }

    this.openUnauthorizedDialog(
      'Your session has expired. You will be redirected to the Login page',
      () => this.authService.signOut()
    );
  }

  /**
   *
   * @param message
   * @param callback
   */
  private openUnauthorizedDialog(message: string, callback: () => void): void {
    const alertDlg = this.commonService.openAlertDialog('', message, true);
    this.isUserUnauthorized = true;
    alertDlg.afterClosed().subscribe({
      next: callback
    });
  }

  /**
   * handleResponseError
   * @param error
   * @param request
   * @param next
   * @returns
   */
  handleResponseError(error: any, request?: any): Observable<any> {
    return throwError(() => {
      this.errorMessages(request, error);
      return error;
    });
  }

  /**
   *
   * @param request
   * @returns
   */
  addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    // External assistant branch only: avoids duplicating dev locals (externalToken, etc.)
    if (request.url.includes('/api/External/external-token')) {
      return request;
    }
    const isExternalAssistantAiRequest =
      (
        request.url.includes('/api/AIAgentCatalog/chat') ||
        request.url.includes('/api/AIAgentCatalog/thread') ||
        request.url.includes('/api/agentBuilder/checkAgentAttachmentSupport') ||
        request.url.includes('/api/agentBuilder/uploadChatFile') ||
        request.url.includes('/api/agentBuilder/deleteChatFiles')
      ) &&
      this.externalAssistantAuthService.isExternalAssistantRoute();
    const externalAssistantBearer = isExternalAssistantAiRequest ? this.externalAssistantAuthService.getStoredExternalToken() : '';
    if (externalAssistantBearer) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${externalAssistantBearer}`
        }
      });
    }

    // Existing guest / external flows — must run before portal B2C so a cached
    // portal session does not break anonymous file-claim (works in incognito).
    let externalToken: any = localStorage.getItem('XAuserDetailsExternal');
    externalToken = JSON.parse(externalToken);
    const externalOnboardingToken = localStorage.getItem('externalToken');
    const userDetails = localStorage.getItem('XAuserDetails');
    const userDetailToken = JSON.parse(userDetails);
    if (
      externalToken?.idToken &&
      (window.location.href.includes('/quote-assessment/submit-missing-info') ||
        (window.location.href.includes('/file-claim/') && !request.url.includes('blob.core')))
    ) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${externalToken.idToken}`
        }
      });
    }

    if (this.authService.loggedIn && this.authService.currentUser) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.authService.currentUser.token}`
        }
      });
    }

    if (
      (window.location.href.includes('/onboarding') ||
        window.location.href.includes('/trial-account') ||
        window.location.href.includes('/agentix-trial-signup')) &&
      externalOnboardingToken
    ) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${externalOnboardingToken}`,
          'Ocp-Apim-Subscription-Key': `${environment.guestSubscriptionKey}`
        }
      });
    }

    if (window.location.href.includes('/claim/assignment') && userDetailToken?.idToken) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${userDetailToken?.idToken}`
        }
      });
    }

    return request;
  }

  /**
   *
   * @param request
   * @returns
   */
  addTenantHeader(request: HttpRequest<any>): HttpRequest<any> {
    // Guest FNOL must not send a previously cached portal tenant
    if (typeof window !== 'undefined' && window.location.href.includes('/file-claim/')) {
      return request;
    }
    const tenantInfo = localStorage.getItem('tenant');
    if (tenantInfo && !request.url.includes('PlatformConfiguration') && !request.url.includes('application-feature')) {
      const tenant = JSON.parse(tenantInfo);
      return request.clone({
        setHeaders: {
          Tenantid: `${tenant.TenantID}`,
          Accountid: `${tenant.AccountID}`,
          Orgid: `${tenant.orgID}`
        }
      });
    }
    return request;
  }

  /**
   *
   * @param request
   * @returns
   */
  addDomainHeader(request: HttpRequest<any>): HttpRequest<any> {
    if (request.url.toLowerCase().includes('/inspection') && !request.url.toLowerCase().includes('inspectiontemplate?domainid')) {
      let domainId = '2100';
      if (this.router.url.includes('repair')) {
        domainId = '3100';
      } else if (this.router.url.includes('/file-claim/')) {
        domainId = '1100';
      } else if (this.router.url.includes('claim')) {
        domainId = '4100';
      }
      return request.clone({
        setHeaders: {
          domainId
        }
      });
    }
    return request;
  }

  /**
   * intercept http request
   * @param req
   * @param next
   * @returns
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const timeoutValue = this.defaultTimeout;
    const timeoutValueNumeric = Number(timeoutValue);
    const langCode = localStorage.getItem('languageCode') || 'en';
    const isGuestApiRequest = req.url.includes(environment.guestApiUrl);
    const subscriptionKey = isGuestApiRequest ? environment.guestSubscriptionKey : environment.subscriptionKey;
    let authReq = req;
    let request = req;
    if (!req.url.includes(`${environment.guestApiUrl}/oauth2/v2.0/token`)) {
      authReq = req.clone({
        headers: req.headers.set('Ocp-Apim-Subscription-Key', subscriptionKey).set('Language-Code', langCode)
      });
      const tenantReq = this.addTenantHeader(authReq);
      let domainReq = null;
      if (!req.headers.get('domainid')) {
        domainReq = this.addDomainHeader(tenantReq);
      } else {
        domainReq = tenantReq;
      }
      request = this.addAuthHeader(domainReq);
    }

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        return event;
      }),
      timeout(timeoutValueNumeric),
      catchError((error) => {
        const source = localStorage.getItem('XAExternalSource');
        const tenantCode = this.externalAssistantAuthService.getExternalAssistantTenantCode();
        // Exclude the token endpoint itself: re-fetching a token after the token call 401s would recurse forever.
        const isExternalAssistant401 =
          this.externalAssistantAuthService.isExternalAssistantRoute() &&
          error.status === 401 &&
          !!tenantCode &&
          !req.url.includes('/api/External/external-token');
        const wasRetried = req.headers.get('X-External-Token-Retry') === 'true';
        if (isExternalAssistant401 && !wasRetried) {
          return this.externalAssistantAuthService.fetchAndStoreTokenByTenantCode(tenantCode).pipe(
            switchMap((tokenResponse) => {
              const accessToken = tokenResponse?.accessToken || '';
              if (!accessToken) {
                return this.handleResponseError(error, authReq);
              }
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${accessToken}`,
                  'Ocp-Apim-Subscription-Key': subscriptionKey,
                  'X-External-Token-Retry': 'true'
                }
              });
              return next.handle(retryReq);
            }),
            catchError(() => {
              this.commonService.hideLoading();
              return this.handleResponseError(error, authReq);
            })
          );
        } else if (location.href.includes('/file-claim/') && error.status === 401 && source) {
          return this.coreService.getExternalToken(source).pipe(
            switchMap((res) => {
              const token = { idToken: res?.data.accessToken };
              localStorage.setItem('XAuserDetails', JSON.stringify(token));
              localStorage.setItem('XAuserDetailsExternal', JSON.stringify(token));

              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${token.idToken}`, 'Ocp-Apim-Subscription-Key': environment.subscriptionKey }
              });
              return next.handle(retryReq);
            }),
            catchError(() => {
              this.commonService.hideLoading();
              return this.handleResponseError(error, authReq);
            })
          );
        } else {
          this.commonService.hideLoading();
          if (error.name === 'TimeoutError' && !error.status) {
            error.status = 524;
            error.url = req.url;
          }
          return this.handleResponseError(error, authReq);
        }
      })
    );
  }
}
